/**
 * Matriz RBAC — espejo en código de docs/roles-permisos.md (fuente de verdad documental).
 * La matriz por ROL es la PLANTILLA: cada usuario parte de la de su rol y la dueña puede darle o quitarle permisos
 * uno por uno (tabla `usuario_permisos`, enmienda 2026-10-09 de la spec 01). El middleware `requierePermiso` y los
 * casos de uso consultan los permisos EFECTIVOS del usuario con `puede`; un test verifica que la tabla
 * `roles_permisos` coincide con esta matriz.
 *
 * Niveles: 'total' (✔) · 'lectura' (L) · 'propone' (P) · ausente = sin acceso (—).
 * Los calificadores de la matriz ("su caja", "en tienda", "aprueba") se aplican dentro del
 * caso de uso, no aquí.
 */
export const ROLES = ['superadmin', 'admin_sucursal', 'cajero', 'almacen', 'contador'] as const;
export type Rol = (typeof ROLES)[number];

export const NIVELES = ['total', 'lectura', 'propone'] as const;
export type Nivel = (typeof NIVELES)[number];

/** Roles cuyo sucursal_id puede ser null (alcance global / multi-sucursal). */
export const ROLES_GLOBALES: readonly Rol[] = ['superadmin', 'contador'];

export const ROL_ETIQUETA: Record<Rol, string> = {
  superadmin: 'Dueña (superadmin)',
  admin_sucursal: 'Administrador de sucursal',
  cajero: 'Cajero',
  almacen: 'Almacén',
  contador: 'Contador',
};

type Celda = Nivel | undefined;
type Fila = readonly [Celda, Celda, Celda, Celda, Celda]; // superadmin, admin_sucursal, cajero, almacen, contador

const T: Nivel = 'total';
const L: Nivel = 'lectura';
const P: Nivel = 'propone';
const _ = undefined;

/**
 * Plantilla por rol (respuestas de la clienta 2026-10-09): el administrador de sucursal no cambia precios, no da
 * crédito, no aprueba ajustes y no entra a compras ni gastos; el contador solo ve lo fiscal.
 */
const MATRIZ_FILAS = {
  'cotizaciones.crear': [T, T, T, _, _],
  'ventas.emitir': [T, T, T, _, L],
  'ventas.descuento': [T, T, _, _, _],
  'ventas.anular': [T, T, _, _, _],
  'ventas.vender-deposito': [T, T, _, _, _],
  'ventas.cliente-vencido': [T, T, _, _, _],
  'ventas.espera': [T, T, T, _, _],
  // Respuestas del 29-10-2026: nota de crédito total + factura nueva al cliente nuevo pagada con ese crédito. Por plantilla
  // solo la dueña; ella lo delega al administrador desde la pantalla de permisos (sin el permiso, autorización «otro_nombre»)
  'ventas.otro-nombre': [T, _, _, _, _],
  'pagos.cobrar': [T, T, T, _, _],
  'pagos.anular-referencia': [T, T, _, _, _],
  'pagos.precargar': [T, _, _, _, _],
  'clientes.crear': [T, T, T, _, L],
  'clientes.credito': [T, _, _, _, _],
  // Respuestas del 29-10-2026: pasar el saldo de una nota de crédito a otro cliente y bajar un saldo duplicado. Solo la
  // dueña por plantilla, delegables; sin el permiso, con la autorización de siempre
  'clientes.traspasar-saldo': [T, _, _, _, _],
  'clientes.ajustar-saldo': [T, _, _, _, _],
  'productos.consultar': [T, T, T, T, T],
  'productos.precios': [T, _, _, _, _],
  'productos.minimos': [T, _, _, _, _],
  'productos.descuentos': [T, _, _, _, _],
  'productos.costos': [T, _, _, _, L],
  'inventario.entregas': [T, T, T, T, _],
  // Respuesta de la dueña del 26-10-2026: una factura «base + 25 %» con la retención por recibir no se entrega; la
  // dueña o el administrador pueden autorizarla (los demás piden la autorización «Entrega con la retención por recibir»)
  'inventario.entrega-sin-retencion': [T, T, _, _, _],
  // Respuestas del 29-10-2026 (Fase 4 de retiros): la orden de retiro en otra sucursal la autoriza la dueña; por
  // plantilla solo ella, y lo delega al administrador de la tienda donde se vendió (sin el permiso, la autorización
  // «retiro_otra_sucursal» con clave o a distancia)
  'inventario.retiro-otra-sucursal': [T, _, _, _, _],
  'inventario.ajustes': [T, P, _, P, _],
  'inventario.conteos': [T, T, _, T, _],
  // Respuesta de la dueña del 2026-10-15: reporta cualquiera con acceso al inventario (también el cajero, que suele
  // verlo primero); aprueba y pone el precio solo ella (inventario.ajustes total y productos.precios)
  'inventario.reportar': [T, T, T, T, _],
  'traslados.despachar': [T, T, _, T, _],
  'flota.gestionar': [T, T, _, _, _],
  'devoluciones.registrar': [T, T, T, _, _],
  'devoluciones.modo-cambio': [T, T, _, _, _],
  'devoluciones.fuera-plazo': [T, T, _, _, _],
  'devoluciones.reintegro-dinero': [T, _, _, _, _],
  'compras.registrar': [T, _, _, _, L],
  'retenciones-clientes.cargar': [T, T, T, _, T],
  'retenciones.reintegro-validar': [T, T, _, _, _],
  'retenciones.corregir-comprobante': [T, _, _, _, T],
  'caja.abrir': [T, T, T, _, _],
  'caja.cerrar-con-diferencia': [T, T, _, _, _],
  'caja.confirmar-retiro': [T, _, _, _, _],
  'caja.reabrir': [T, _, _, _, _],
  'gastos.registrar': [T, _, _, _, _],
  'reportes.sucursal': [T, T, _, _, _],
  'reportes.fiscal': [T, T, _, _, T],
  'reportes.consolidado': [T, _, _, _, _],
  'libros.ver': [T, L, _, _, T],
  'libros.editar-retenciones': [T, _, _, _, T],
  'libros.facturas-manuales': [T, T, _, _, T],
  'autorizaciones.resolver': [T, T, _, _, _],
  'tasas.actualizar': [T, _, _, _, _],
  'usuarios.admin': [T, _, _, _, _],
  'auditoria.ver': [T, L, _, _, L],
} as const satisfies Record<string, Fila>;

export type Permiso = keyof typeof MATRIZ_FILAS;
export const PERMISOS = Object.keys(MATRIZ_FILAS) as Permiso[];

/**
 * Permisos que NINGUNA ruta ni caso de uso consulta: darlos o quitarlos no cambia nada. «Cobros: liberar una referencia»
 * (no hay pantalla para liberar una referencia) y «Libros: editar retenciones» (corregir un comprobante depende de
 * «Retenciones: corregir comprobantes»). Las claves se conservan en la matriz y en la tabla `roles_permisos` por lo que
 * ya está guardado (plantillas y permisos propios), pero no salen en la pantalla de permisos ni cuentan al administrar
 * usuarios con el permiso delegado (re-auditoría 2026-10-18, ronda 2, hallazgos 1-H13 y 6-H12).
 */
export const PERMISOS_SIN_USO: readonly Permiso[] = ['pagos.anular-referencia', 'libros.editar-retenciones'];
/** Los permisos que hacen algo: los de la pantalla de permisos por usuario. */
export const PERMISOS_EN_USO: readonly Permiso[] = PERMISOS.filter((p) => !PERMISOS_SIN_USO.includes(p));

/** Texto de cada permiso para la pantalla de permisos por usuario. */
export const PERMISO_ETIQUETA: Record<Permiso, string> = {
  'cotizaciones.crear': 'Cotizaciones: crear, editar y re-tasar',
  'ventas.emitir': 'Ventas: emitir facturas y notas (lectura: ver documentos)',
  'ventas.descuento': 'Ventas: aplicar descuentos',
  'ventas.anular': 'Ventas: anular y reemitir por hoja dañada',
  'ventas.vender-deposito': 'Ventas: vender contra un depósito',
  'ventas.cliente-vencido': 'Ventas: vender a un cliente con facturas vencidas',
  'ventas.espera': 'Ventas: poner y retomar en espera',
  'ventas.otro-nombre': 'Ventas: facturar a otro nombre (nota de crédito y factura nueva)',
  'pagos.cobrar': 'Cobros y abonos',
  'pagos.anular-referencia': 'Cobros: liberar una referencia',
  'pagos.precargar': 'Cobros: precargar Zelle y Binance recibidos',
  'clientes.crear': 'Clientes: crear y editar datos básicos',
  'clientes.credito': 'Clientes: crédito, plazo y lista de precios',
  'clientes.traspasar-saldo': 'Clientes: traspasar el saldo de una nota de crédito a otro cliente',
  'clientes.ajustar-saldo': 'Clientes: bajar el saldo a favor (ajuste)',
  'productos.consultar': 'Productos: consultar',
  'productos.precios': 'Productos: fichas, presentaciones y precios',
  'productos.minimos': 'Productos: stock mínimo',
  'productos.descuentos': 'Productos: descuentos programados',
  'productos.costos': 'Productos: ver costos y márgenes',
  'inventario.entregas': 'Entregas a clientes',
  'inventario.entrega-sin-retencion': 'Entregas: entregar una factura con la retención por recibir',
  'inventario.retiro-otra-sucursal': 'Entregas: autorizar el retiro en otra sucursal (orden de retiro)',
  'inventario.ajustes': 'Ajustes de inventario (total: aprueba · propone: los carga)',
  'inventario.conteos': 'Conteos y despieces',
  'inventario.reportar': 'Reportar roturas de vidrio y productos con detalles (los aprueba la dueña)',
  'traslados.despachar': 'Traslados: despachar y recibir',
  'flota.gestionar': 'Vehículos, equipos y mantenimiento',
  'devoluciones.registrar': 'Devoluciones y cambios',
  'devoluciones.modo-cambio': 'Devoluciones: notas de débito',
  'devoluciones.fuera-plazo': 'Devoluciones: aceptar fuera de plazo',
  'devoluciones.reintegro-dinero': 'Devoluciones: devolver dinero',
  'compras.registrar': 'Compras y proveedores',
  'retenciones-clientes.cargar': 'Retenciones de clientes: cargar comprobantes',
  'retenciones.reintegro-validar': 'Retenciones: validar reintegros',
  'retenciones.corregir-comprobante': 'Retenciones: corregir comprobantes',
  'caja.abrir': 'Caja: abrir y cerrar la suya',
  'caja.cerrar-con-diferencia': 'Caja: autorizar cierres con diferencia',
  'caja.confirmar-retiro': 'Caja: confirmar retiros parciales',
  'caja.reabrir': 'Caja: reabrir una caja cerrada',
  'gastos.registrar': 'Gastos',
  'reportes.sucursal': 'Reportes de la sucursal',
  'reportes.fiscal': 'Reportes fiscales (IGTF, compras, cuentas por pagar)',
  'reportes.consolidado': 'Consolidado de todas las sucursales',
  'libros.ver': 'Libros de ventas y compras',
  'libros.editar-retenciones': 'Libros: editar retenciones',
  'libros.facturas-manuales': 'Libros: registrar facturas manuales',
  'autorizaciones.resolver': 'Autorizaciones: bandeja de solicitudes',
  'tasas.actualizar': 'Tasas del día',
  'usuarios.admin': 'Usuarios, sucursales y configuración',
  'auditoria.ver': 'Auditoría',
};

/** Matriz completa como Record<Permiso, Record<Rol, Nivel | undefined>>. */
export const MATRIZ: Record<Permiso, Record<Rol, Nivel | undefined>> = Object.fromEntries(
  (Object.entries(MATRIZ_FILAS) as [Permiso, Fila][]).map(([permiso, fila]) => [
    permiso,
    Object.fromEntries(ROLES.map((rol, i) => [rol, fila[i]])) as Record<Rol, Nivel | undefined>,
  ]),
) as Record<Permiso, Record<Rol, Nivel | undefined>>;

/** Nivel de la PLANTILLA del rol para el permiso, o undefined si no tiene acceso. */
export function nivelDe(rol: Rol, permiso: Permiso): Nivel | undefined {
  return MATRIZ[permiso][rol];
}

function satisface(nivel: Nivel | undefined, requerido: Nivel): boolean {
  if (!nivel) return false;
  if (requerido === 'total') return nivel === 'total';
  if (requerido === 'propone') return nivel === 'total' || nivel === 'propone';
  return true;
}

/**
 * ¿La PLANTILLA del rol satisface el nivel mínimo? Orden: total > propone > lectura.
 * Para decidir sobre un usuario concreto usa `puede` (respeta sus permisos propios).
 */
export function tienePermiso(rol: Rol, permiso: Permiso, requerido: Nivel = 'total'): boolean {
  return satisface(nivelDe(rol, permiso), requerido);
}

/** Permiso propio de un usuario: `nivel` null = se lo quitaron; con nivel = se lo dieron (o cambiaron). */
export interface PermisoUsuario {
  permiso: Permiso;
  nivel: Nivel | null;
}

/** Permisos efectivos: solo aparecen los que el usuario tiene, con su nivel. */
export type PermisosEfectivos = Partial<Record<Permiso, Nivel>>;

/**
 * Plantilla del rol + permisos propios del usuario. La dueña (superadmin) conserva siempre su plantilla completa:
 * nadie puede dejarla sin acceso.
 */
export function permisosEfectivos(rol: Rol, propios: readonly PermisoUsuario[] = []): PermisosEfectivos {
  const salida: PermisosEfectivos = {};
  for (const permiso of PERMISOS) {
    const nivel = MATRIZ[permiso][rol];
    if (nivel) salida[permiso] = nivel;
  }
  if (rol === 'superadmin') return salida;
  for (const p of propios) {
    if (!(p.permiso in MATRIZ)) continue;
    if (p.nivel === null) delete salida[p.permiso];
    else salida[p.permiso] = p.nivel;
  }
  return salida;
}

/** Sujeto de una decisión de permiso: su rol y, si ya se calcularon, sus permisos efectivos. */
export interface SujetoPermisos {
  rol: Rol;
  permisos?: PermisosEfectivos | undefined;
}

/** ¿El usuario puede? Mira sus permisos efectivos; si no vienen, cae en la plantilla del rol. */
export function puede(sujeto: SujetoPermisos, permiso: Permiso, requerido: Nivel = 'total'): boolean {
  const nivel = sujeto.permisos ? sujeto.permisos[permiso] : nivelDe(sujeto.rol, permiso);
  return satisface(nivel, requerido);
}

/** Lista plana (rol, permiso, nivel) — para la tabla roles_permisos y su test de igualdad. */
export function matrizPlana(): { rol: Rol; permiso: Permiso; nivel: Nivel }[] {
  const filas: { rol: Rol; permiso: Permiso; nivel: Nivel }[] = [];
  for (const permiso of PERMISOS) {
    for (const rol of ROLES) {
      const nivel = MATRIZ[permiso][rol];
      if (nivel) filas.push({ rol, permiso, nivel });
    }
  }
  return filas;
}

// ─── Autorizaciones (enmienda 2026-10-09 de la spec 01) ─────────────────────
/**
 * Operaciones que un usuario sin el permiso puede hacer con la AUTORIZACIÓN de quien sí lo tiene: con su clave en la
 * misma pantalla o a distancia (solicitud que se aprueba desde otro usuario). Cada tipo dice qué permiso habilita a
 * autorizarla.
 */
export const TIPOS_AUTORIZACION = {
  descuento: { permiso: 'ventas.descuento', etiqueta: 'Descuento en una venta' },
  credito: { permiso: 'clientes.credito', etiqueta: 'Crédito por encima del límite' },
  cliente_vencido: { permiso: 'ventas.cliente-vencido', etiqueta: 'Venta a cliente con facturas vencidas' },
  venta_deposito: { permiso: 'ventas.vender-deposito', etiqueta: 'Venta contra depósito' },
  anulacion: { permiso: 'ventas.anular', etiqueta: 'Anular y reemitir un documento' },
  devolucion_fuera_plazo: { permiso: 'devoluciones.fuera-plazo', etiqueta: 'Devolución fuera de plazo' },
  devolucion_dinero: { permiso: 'devoluciones.reintegro-dinero', etiqueta: 'Devolución de dinero' },
  cierre_diferencia: { permiso: 'caja.cerrar-con-diferencia', etiqueta: 'Cierre de caja con diferencia' },
  retiro_caja: { permiso: 'caja.confirmar-retiro', etiqueta: 'Retiro parcial de caja' },
  entrega_sin_retencion: { permiso: 'inventario.entrega-sin-retencion', etiqueta: 'Entrega con la retención por recibir' },
  otro_nombre: { permiso: 'ventas.otro-nombre', etiqueta: 'Facturar a otro nombre' },
  traspaso_saldo: { permiso: 'clientes.traspasar-saldo', etiqueta: 'Traspasar saldo a otro cliente' },
  ajuste_saldo: { permiso: 'clientes.ajustar-saldo', etiqueta: 'Ajustar (bajar) el saldo a favor' },
  retiro_otra_sucursal: { permiso: 'inventario.retiro-otra-sucursal', etiqueta: 'Retiro en otra sucursal' },
} as const satisfies Record<string, { permiso: Permiso; etiqueta: string }>;
export type TipoAutorizacion = keyof typeof TIPOS_AUTORIZACION;
export const TIPOS_AUTORIZACION_LISTA = Object.keys(TIPOS_AUTORIZACION) as TipoAutorizacion[];
/**
 * Los que se pueden pedir hoy. «Retiro parcial de caja» queda solo por las autorizaciones viejas: el retiro se confirma
 * con el permiso de confirmar retiros, sin autorización (hallazgo E9 del 2026-10-18).
 */
export const TIPOS_AUTORIZACION_SOLICITABLES = TIPOS_AUTORIZACION_LISTA.filter((t) => t !== 'retiro_caja');

/**
 * Módulos del sidebar (skill diseno-visual-mg). Cada módulo declara el permiso que lo
 * habilita; el sidebar muestra solo los que el usuario tiene (cualquier nivel).
 */
export interface ModuloUI {
  id: string;
  titulo: string;
  ruta: string;
  permiso: Permiso;
  /** Nivel mínimo para verlo (default lectura = cualquier nivel). */
  nivel?: Nivel;
  /** Si viene, el módulo se ve con CUALQUIERA de estos permisos (pantallas que reúnen varias funciones). */
  alguno?: readonly Permiso[];
}

/**
 * Permisos con que se entra a Reportes: los mismos que acepta la API (cada pestaña pide el suyo). Con solo «Reportes de
 * la sucursal» se veía «Sin acceso» (re-auditoría 2026-10-18, ronda 2, hallazgo 6-H11).
 */
export const PERMISOS_REPORTES: readonly Permiso[] = ['reportes.sucursal', 'reportes.fiscal', 'reportes.consolidado'];

export const MODULOS_UI: readonly ModuloUI[] = [
  { id: 'panel', titulo: 'Panel general', ruta: '/', permiso: 'productos.consultar' },
  { id: 'pos', titulo: 'Ventas (POS)', ruta: '/pos', permiso: 'ventas.emitir', nivel: 'total' },
  { id: 'cotizaciones', titulo: 'Cotizaciones', ruta: '/cotizaciones', permiso: 'cotizaciones.crear' },
  { id: 'documentos', titulo: 'Documentos', ruta: '/documentos', permiso: 'ventas.emitir' },
  { id: 'entregas', titulo: 'Entregas', ruta: '/entregas', permiso: 'inventario.entregas' },
  { id: 'devoluciones', titulo: 'Devoluciones', ruta: '/devoluciones', permiso: 'devoluciones.registrar' },
  { id: 'productos', titulo: 'Productos', ruta: '/productos', permiso: 'productos.consultar' },
  { id: 'inventario', titulo: 'Inventario', ruta: '/inventario', permiso: 'inventario.conteos', nivel: 'propone' },
  { id: 'ajustes', titulo: 'Ajustes', ruta: '/ajustes', permiso: 'inventario.ajustes', nivel: 'propone' },
  { id: 'roturas', titulo: 'Roturas y detalles', ruta: '/roturas', permiso: 'inventario.reportar' },
  { id: 'traslados', titulo: 'Traslados', ruta: '/traslados', permiso: 'traslados.despachar' },
  { id: 'flota', titulo: 'Flota y equipos', ruta: '/flota', permiso: 'flota.gestionar' },
  { id: 'compras', titulo: 'Compras', ruta: '/compras', permiso: 'compras.registrar' },
  // Módulo propio: así quien solo recibe el permiso de gastos puede entrar (los gastos los lleva la dueña)
  { id: 'gastos', titulo: 'Gastos', ruta: '/gastos', permiso: 'gastos.registrar' },
  { id: 'retenciones', titulo: 'Retenciones', ruta: '/retenciones', permiso: 'retenciones-clientes.cargar' },
  { id: 'clientes', titulo: 'Clientes', ruta: '/clientes', permiso: 'clientes.crear' },
  { id: 'pagos-recibidos', titulo: 'Zelle y Binance', ruta: '/pagos-recibidos', permiso: 'pagos.precargar' },
  { id: 'caja', titulo: 'Caja', ruta: '/caja', permiso: 'caja.abrir' },
  { id: 'cierres', titulo: 'Cierres de caja', ruta: '/cierres', permiso: 'caja.abrir' },
  { id: 'autorizaciones', titulo: 'Autorizaciones', ruta: '/autorizaciones', permiso: 'autorizaciones.resolver' },
  { id: 'reportes', titulo: 'Reportes', ruta: '/reportes', permiso: 'reportes.sucursal', alguno: PERMISOS_REPORTES },
  { id: 'libros', titulo: 'Libros', ruta: '/libros', permiso: 'libros.ver' },
  { id: 'auditoria', titulo: 'Auditoría', ruta: '/auditoria', permiso: 'auditoria.ver' },
  // Lo mismo que pide su tablero (re-auditoría 2026-10-18, ronda 2, hallazgo 7-H8): antes pedía «Usuarios, sucursales y
  // configuración» y con permisos por usuario se veía el menú con un tablero negado, o al revés
  { id: 'consolidado', titulo: 'Consolidado', ruta: '/consolidado', permiso: 'reportes.consolidado' },
  { id: 'configuracion', titulo: 'Configuración', ruta: '/configuracion', permiso: 'usuarios.admin', nivel: 'total' },
];

/** Módulos visibles para un usuario (según sus permisos efectivos). */
export function modulosPara(sujeto: SujetoPermisos): ModuloUI[] {
  return MODULOS_UI.filter((m) => [m.permiso, ...(m.alguno ?? [])].some((p) => puede(sujeto, p, m.nivel ?? 'lectura')));
}

/** Módulos de la PLANTILLA de un rol (documentación y tests). */
export function modulosParaRol(rol: Rol): ModuloUI[] {
  return modulosPara({ rol });
}
